<script>
import { required } from "vuelidate/lib/validators";
import validationMessages from "@/components/validations";
import Swal from "sweetalert2";
import General from "@/services/General";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";

function createEmptySubItem() {
  return {
    id: null,
    title: "",
    subtitle: "",
    type: "link",
    url: "",
    existingUrl: "",
    pdfFile: null,
    pdfFileName: "",
    deepLinkingAction: "",
    deepLinkingFullScreen: "Yes",
    second_level_key: "",
    second_level_title: "",
    secondLevelOptions: [],
    selectedSecondLevel: "",
    showSecondLevel: false,
  };
}

export default {
  components: {
    validationMessages,
    Multiselect,
  },
  props: {
    linkData: {
      type: Object,
      default: null,
    },
  },
  validations: {
    title: { required },
  },
  data() {
    return {
      csrf_token: localStorage.getItem("csrf_token"),

      showModal: false,
      tryingToSubmit: false,
      submitted: false,
      isLoading: false,

      editId: null,
      hasParent: false,
      linkMode: "standalone",
      selectedType: "link",
      title: "",
      subtitle: "",
      url: "",
      existingUrl: "",

      methodOption: [],
      deepLinkActions: [],

      deepLinkingAction: "",
      showSecondLevel: false,
      second_level_key: "",
      second_level_title: "",
      secondLevelOptions: [],
      selectedSecondLevel: "",
      deepLinkingFullScreen: "Yes",

      pdfFile: null,
      pdfFileName: "",

      placementOptions: [],
      selectedPlacements: [],

      subItems: [createEmptySubItem()],

      typeOptions: [
        { value: "link", text: "link" },
        { value: "deeplink", text: "deeplink" },
        { value: "pdf", text: "pdf" },
      ],
      subItemTypeOptions: [
        { value: "link", text: "link" },
        { value: "deeplink", text: "deeplink" },
        { value: "pdf", text: "pdf" },
      ],
    };
  },

  computed: {
    isGroup() {
      return this.linkMode === "group";
    },
    isStandalone() {
      return this.linkMode === "standalone";
    },
    showPlacements() {
      return !this.hasParent && (this.isGroup || this.isStandalone);
    },
    showLinkFields() {
      return this.isStandalone && this.selectedType === "link";
    },
    showDeeplinkFields() {
      return this.isStandalone && this.selectedType === "deeplink";
    },
    showPdfFields() {
      return this.isStandalone && this.selectedType === "pdf";
    },
    modalTitle() {
      return this.isGroup ? "Edit Group" : "Edit Application Link";
    },
  },

  created() {
    this.getDeeplinkCampaignMethods();
  },

  methods: {
    closeModal() {
      this.showModal = false;
      this.resetProps();
    },

    resetProps() {
      this.tryingToSubmit = false;
      this.submitted = false;
      this.isLoading = false;
      this.editId = null;
      this.hasParent = false;
      this.linkMode = "standalone";
      this.selectedType = "link";
      this.title = "";
      this.subtitle = "";
      this.url = "";
      this.existingUrl = "";
      this.deepLinkingAction = "";
      this.showSecondLevel = false;
      this.second_level_key = "";
      this.second_level_title = "";
      this.secondLevelOptions = [];
      this.selectedSecondLevel = "";
      this.deepLinkingFullScreen = "Yes";
      this.pdfFile = null;
      this.pdfFileName = "";
      this.selectedPlacements = [];
      this.subItems = [createEmptySubItem()];
      this.$v.$reset();
    },

    refreshScreen() {
      this.$emit("refreshScreen");
    },

    detectTypeFromUrl(url, type) {
      if (
        type === "link" &&
        url &&
        /^[a-z0-9]+dl:\/\//i.test(String(url))
      ) {
        return "deeplink";
      }
      return type || "link";
    },

    parseDeeplinkUrl(url) {
      if (!url || !String(url).includes("?")) {
        return { action: "", fullscreen: "Yes", secondLevelKey: "", secondLevelValue: "" };
      }
      const query = String(url).split("?")[1] || "";
      const params = new URLSearchParams(query);
      let fullscreen = params.get("fullscreen") || "Yes";
      if (fullscreen === "true") fullscreen = "Yes";
      if (fullscreen === "false") fullscreen = "No";

      let secondLevelKey = "";
      let secondLevelValue = "";
      params.forEach((value, key) => {
        if (key !== "action" && key !== "fullscreen") {
          secondLevelKey = key;
          secondLevelValue = value;
        }
      });

      return {
        action: params.get("action") || "",
        fullscreen,
        secondLevelKey,
        secondLevelValue,
      };
    },

    async getAvailablePlacements() {
      try {
        const response = await General.getAvailableApplicationLinkPlacements();
        const data = response.data?.data ?? [];
        this.placementOptions = Array.isArray(data) ? data : [];
      } catch (e) {
        this.placementOptions = [];
      }
    },

    async getDeeplinkCampaignMethods() {
      try {
        const response = await General.getDeeplinkCampaignMethods();
        const data = response.data.data ? response.data.data : [];
        this.methodOption = data;
        this.deepLinkActions = data.map((item) => ({
          action: item.method,
          name: item.description,
        }));
      } catch (e) {
        this.methodOption = [];
        this.deepLinkActions = [];
      }
    },

    async loadDeeplinkSecondLevel(second_level, target, selectedValue) {
      if (!second_level) {
        target.secondLevelOptions = [];
        target.showSecondLevel = false;
        target.selectedSecondLevel = "";
        return;
      }

      try {
        const response = await General.getDeeplinkSecondLevelOptions(second_level);
        target.secondLevelOptions = response.data.data ? response.data.data : [];
        target.showSecondLevel = target.secondLevelOptions.length > 0;
        if (selectedValue) {
          target.selectedSecondLevel = selectedValue;
        }
      } catch (e) {
        target.secondLevelOptions = [];
        target.showSecondLevel = false;
      }
    },

    async applyDeeplinkToTarget(target, url) {
      const parsed = this.parseDeeplinkUrl(url);
      target.deepLinkingAction = parsed.action;
      target.deepLinkingFullScreen = parsed.fullscreen;

      const method = this.methodOption.find((m) => m.method === parsed.action);
      target.second_level_key = method?.second_level || "";
      target.second_level_title = method?.second_level_description || "";

      if (target.second_level_key) {
        await this.loadDeeplinkSecondLevel(
          target.second_level_key,
          target,
          parsed.secondLevelValue
        );
      } else {
        target.showSecondLevel = false;
        target.selectedSecondLevel = "";
      }
    },

    async mapLinkToSubItem(link) {
      const sub = createEmptySubItem();
      sub.id = link.id;
      sub.title = link.title || "";
      sub.subtitle = link.subtitle || "";
      sub.type = this.detectTypeFromUrl(link.url, link.type);
      sub.existingUrl = link.url || "";

      if (sub.type === "link") {
        sub.url = link.url || "";
      } else if (sub.type === "deeplink") {
        await this.applyDeeplinkToTarget(sub, link.url);
      } else if (sub.type === "pdf") {
        sub.pdfFileName = "";
      }

      return sub;
    },

    async loadStandaloneLink(link) {
      this.linkMode = "standalone";
      this.editId = link.id;
      this.hasParent = !!link.parent;
      this.title = link.title || "";
      this.subtitle = link.subtitle || "";
      this.selectedType = this.detectTypeFromUrl(link.url, link.type);
      this.existingUrl = link.url || "";
      this.selectedPlacements = Array.isArray(link.placements) ? [...link.placements] : [];

      if (this.selectedType === "link") {
        this.url = link.url || "";
      } else if (this.selectedType === "deeplink") {
        await this.applyDeeplinkToTarget(this, link.url);
      } else if (this.selectedType === "pdf") {
        this.pdfFileName = "";
      }
    },

    async loadGroupLink(categoryId) {
      const [categoryRes, childrenRes] = await Promise.all([
        General.getApplicationLinkById(categoryId),
        General.getApplicationLinkChildren(categoryId),
      ]);

      const category = categoryRes.data?.links?.[0];
      if (!category) {
        throw new Error("Category not found");
      }

      const children = Array.isArray(childrenRes.data?.links)
        ? childrenRes.data.links.sort((a, b) => a.order - b.order)
        : [];

      this.linkMode = "group";
      this.editId = category.id;
      this.title = category.title || "";
      this.subtitle = category.subtitle || "";
      this.selectedPlacements = Array.isArray(category.placements)
        ? [...category.placements]
        : [];

      const mapped = await Promise.all(children.map((child) => this.mapLinkToSubItem(child)));
      this.subItems = mapped.length ? mapped : [createEmptySubItem()];
    },

    async onModalShown() {
      if (!this.linkData?.id) {
        return;
      }

      this.isLoading = true;
      try {
        await this.getAvailablePlacements();

        if (this.linkData.type === "category") {
          await this.loadGroupLink(this.linkData.id);
        } else {
          const response = await General.getApplicationLinkById(this.linkData.id);
          const link = response.data?.links?.[0] || this.linkData;
          await this.loadStandaloneLink(link);
        }
      } catch (e) {
        const errorMsg =
          e?.response?.data?.error || e?.message || "Failed to load link for edit.";
        Swal.fire("Error", errorMsg, "error");
        this.closeModal();
      } finally {
        this.isLoading = false;
      }
    },

    onStandaloneTypeChange() {
      if (this.selectedType === "link") {
        this.url =
          this.existingUrl && !/^[a-z0-9]+dl:\/\//i.test(this.existingUrl)
            ? this.existingUrl
            : "";
        this.pdfFile = null;
        this.pdfFileName = "";
      } else if (this.selectedType === "deeplink") {
        this.url = "";
        this.pdfFile = null;
        this.pdfFileName = "";
        if (this.existingUrl && /^[a-z0-9]+dl:\/\//i.test(this.existingUrl)) {
          this.applyDeeplinkToTarget(this, this.existingUrl);
        } else {
          this.deepLinkingAction = "";
          this.showSecondLevel = false;
          this.selectedSecondLevel = "";
        }
      } else if (this.selectedType === "pdf") {
        this.url = "";
        this.deepLinkingAction = "";
        this.showSecondLevel = false;
        this.selectedSecondLevel = "";
        this.pdfFileName = "";
      }
    },

    onSubItemTypeChange(subItem) {
      if (subItem.type === "link") {
        subItem.url =
          subItem.existingUrl &&
          !/^[a-z0-9]+dl:\/\//i.test(subItem.existingUrl)
            ? subItem.existingUrl
            : "";
        subItem.pdfFile = null;
        subItem.pdfFileName = "";
      } else if (subItem.type === "deeplink") {
        subItem.url = "";
        subItem.pdfFile = null;
        subItem.pdfFileName = "";
        if (
          subItem.existingUrl &&
          /^[a-z0-9]+dl:\/\//i.test(subItem.existingUrl)
        ) {
          this.applyDeeplinkToTarget(subItem, subItem.existingUrl);
        } else {
          subItem.deepLinkingAction = "";
          subItem.showSecondLevel = false;
          subItem.selectedSecondLevel = "";
        }
      } else if (subItem.type === "pdf") {
        subItem.url = "";
        subItem.deepLinkingAction = "";
        subItem.showSecondLevel = false;
        subItem.selectedSecondLevel = "";
        subItem.pdfFileName = "";
      }
    },

    onChangeScreen() {
      this.showSecondLevel = false;
      this.secondLevelOptions = [];
      this.selectedSecondLevel = "";

      const selected = this.methodOption.find(
        (m) => m.method === this.deepLinkingAction
      );
      this.second_level_key = selected?.second_level || "";
      this.second_level_title = selected?.second_level_description || "";

      if (this.second_level_key) {
        this.loadDeeplinkSecondLevel(this.second_level_key, this);
      }
    },

    onSubItemChangeScreen(subItem) {
      subItem.showSecondLevel = false;
      subItem.secondLevelOptions = [];
      subItem.selectedSecondLevel = "";

      const selected = this.methodOption.find(
        (m) => m.method === subItem.deepLinkingAction
      );
      subItem.second_level_key = selected?.second_level || "";
      subItem.second_level_title = selected?.second_level_description || "";

      if (subItem.second_level_key) {
        this.loadDeeplinkSecondLevel(subItem.second_level_key, subItem);
      }
    },

    onPdfSelected(event) {
      const file = event.target.files?.[0] || null;
      this.pdfFile = file;
      this.pdfFileName = file ? file.name : "";
    },

    onSubItemPdfSelected(event, subItem) {
      const file = event.target.files?.[0] || null;
      subItem.pdfFile = file;
      subItem.pdfFileName = file ? file.name : "";
    },

    addSubItem() {
      this.subItems.push(createEmptySubItem());
    },

    removeSubItem(index) {
      if (this.subItems.length <= 1) {
        return;
      }
      this.subItems.splice(index, 1);
    },

    buildDeepLinkPayload(action, fullscreen, secondLevelKey, selectedSecondLevel) {
      const deepLink = {
        action: action || "",
        fullscreen: fullscreen || "Yes",
      };
      if (selectedSecondLevel && secondLevelKey) {
        deepLink[secondLevelKey] = selectedSecondLevel;
      }
      return deepLink;
    },

    validateStandalone() {
      if (this.showLinkFields && !this.url.trim()) {
        Swal.fire("Missing URL", "Please provide URL.", "warning");
        return false;
      }
      if (this.showPdfFields && !this.pdfFile && !this.existingUrl) {
        Swal.fire("Missing PDF", "Please upload a PDF file.", "warning");
        return false;
      }
      return true;
    },

    validateGroup() {
      if (!this.subItems.length) {
        Swal.fire("Missing sub-items", "Add at least one link to the group.", "warning");
        return false;
      }

      for (let i = 0; i < this.subItems.length; i++) {
        const item = this.subItems[i];
        const label = `Sub-item ${i + 1}`;

        if (!item.title.trim()) {
          Swal.fire("Missing title", `${label}: title is required.`, "warning");
          return false;
        }
        if (item.type === "link" && !item.url.trim()) {
          Swal.fire("Missing URL", `${label}: URL is required.`, "warning");
          return false;
        }
        if (item.type === "pdf" && !item.pdfFile && !item.existingUrl) {
          Swal.fire("Missing PDF", `${label}: PDF file is required.`, "warning");
          return false;
        }
      }

      return true;
    },

    buildStandalonePayload() {
      const payload = {
        title: this.title,
        type: this.selectedType,
      };

      if (!this.hasParent) {
        payload.placements = [...this.selectedPlacements];
      }

      if (this.subtitle) {
        payload.subtitle = this.subtitle;
      }
      if (this.showLinkFields) {
        payload.url = this.url;
      }
      if (this.showDeeplinkFields) {
        payload.deep_link = this.buildDeepLinkPayload(
          this.deepLinkingAction,
          this.deepLinkingFullScreen,
          this.second_level_key,
          this.selectedSecondLevel
        );
      }

      const useMultipart = this.showPdfFields && !!this.pdfFile;
      if (useMultipart) {
        const formData = new FormData();
        formData.append("title", payload.title);
        formData.append("type", payload.type);
        formData.append("csrf_token", this.csrf_token);
        if (payload.placements) {
          payload.placements.forEach((placement) => {
            formData.append("placements[]", placement);
          });
        }
        if (payload.subtitle) {
          formData.append("subtitle", payload.subtitle);
        }
        formData.append("pdf_file", this.pdfFile, this.pdfFileName);
        return { data: formData, isMultipart: true };
      }

      return { data: payload, isMultipart: false };
    },

    buildGroupPayload() {
      const useMultipart = this.subItems.some(
        (item) => item.type === "pdf" && item.pdfFile
      );

      const mapSubItem = (item) => {
        const subItem = {
          title: item.title.trim(),
          type: item.type,
        };
        if (item.id) {
          subItem.id = item.id;
        }
        if (item.subtitle) {
          subItem.subtitle = item.subtitle;
        }
        if (item.type === "link") {
          subItem.url = item.url.trim();
        }
        if (item.type === "deeplink") {
          subItem.deep_link = this.buildDeepLinkPayload(
            item.deepLinkingAction,
            item.deepLinkingFullScreen,
            item.second_level_key,
            item.selectedSecondLevel
          );
        }
        return subItem;
      };

      if (!useMultipart) {
        const payload = {
          title: this.title,
          placements: [...this.selectedPlacements],
          sub_items: this.subItems.map(mapSubItem),
        };
        if (this.subtitle) {
          payload.subtitle = this.subtitle;
        }
        return { data: payload, isMultipart: false };
      }

      const formData = new FormData();
      formData.append("title", this.title);
      formData.append("csrf_token", this.csrf_token);
      if (this.subtitle) {
        formData.append("subtitle", this.subtitle);
      }
      this.selectedPlacements.forEach((placement) => {
        formData.append("placements[]", placement);
      });

      this.subItems.forEach((item, index) => {
        const subItem = mapSubItem(item);
        formData.append(`sub_items[${index}][title]`, subItem.title);
        formData.append(`sub_items[${index}][type]`, subItem.type);
        if (subItem.id) {
          formData.append(`sub_items[${index}][id]`, subItem.id);
        }
        if (subItem.subtitle) {
          formData.append(`sub_items[${index}][subtitle]`, subItem.subtitle);
        }
        if (item.type === "link") {
          formData.append(`sub_items[${index}][url]`, subItem.url);
        }
        if (item.type === "deeplink") {
          formData.append(
            `sub_items[${index}][deep_link][action]`,
            subItem.deep_link.action
          );
          formData.append(
            `sub_items[${index}][deep_link][fullscreen]`,
            subItem.deep_link.fullscreen
          );
          Object.keys(subItem.deep_link).forEach((key) => {
            if (key !== "action" && key !== "fullscreen") {
              formData.append(
                `sub_items[${index}][deep_link][${key}]`,
                subItem.deep_link[key]
              );
            }
          });
        }
        if (item.type === "pdf" && item.pdfFile) {
          formData.append(
            `sub_items[${index}][pdf_file]`,
            item.pdfFile,
            item.pdfFileName
          );
        }
      });

      return { data: formData, isMultipart: true };
    },

    async saveApplicationLink() {
      this.tryingToSubmit = true;
      this.submitted = true;
      this.$v.$touch();

      if (this.$v.$invalid || !this.editId) {
        this.tryingToSubmit = false;
        return;
      }

      if (this.showPlacements && !this.selectedPlacements.length) {
        Swal.fire("Missing Placement", "Please select at least one placement.", "warning");
        this.tryingToSubmit = false;
        return;
      }

      if (this.isStandalone && !this.validateStandalone()) {
        this.tryingToSubmit = false;
        return;
      }

      if (this.isGroup && !this.validateGroup()) {
        this.tryingToSubmit = false;
        return;
      }

      try {
        const { data, isMultipart } = this.isGroup
          ? this.buildGroupPayload()
          : this.buildStandalonePayload();

        const response = await General.updateApplicationLink(
          this.editId,
          data,
          isMultipart
        );

        if (response.data?.warnings?.length) {
          Swal.fire(
            "Updated with warnings",
            response.data.warnings.join("\n"),
            "warning"
          );
        } else {
          Swal.fire(
            "Success",
            this.isGroup ? "Group updated!" : "Application link updated!",
            "success"
          );
        }

        this.closeModal();
        this.refreshScreen();
      } catch (e) {
        const errorMsg =
          e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          "Failed to update application link.";
        Swal.fire("Error", errorMsg, "error");
      } finally {
        this.tryingToSubmit = false;
      }
    },
  },
};
</script>

<template>
  <b-modal
    id="edit_application_link"
    size="lg"
    v-model="showModal"
    title-class="font-18"
    body-class="p-3"
    @hidden="resetProps"
    @shown="onModalShown"
  >
    <template v-slot:modal-header>
      <div class="w-100">
        <h5 class="modal-title mb-0">{{ modalTitle }}</h5>
      </div>
      <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
    </template>

    <div v-if="isLoading" class="text-center py-4">
      <b-spinner variant="primary"></b-spinner>
    </div>

    <form v-else @submit.prevent="saveApplicationLink">
      <div class="card mb-3">
        <div class="card-body py-2">
          <div class="mb-3">
            <label class="control-label form-label">Title *</label>
            <b-form-input
              v-model.trim="title"
              :class="{ 'is-invalid': submitted && $v.title.$error }"
              :placeholder="isGroup ? 'Group title' : 'Enter title'"
            ></b-form-input>
            <validationMessages
              v-if="submitted"
              :fieldName="'Title'"
              :validationName="$v.title"
            ></validationMessages>
          </div>

          <div class="mb-3">
            <label class="control-label form-label">Subtitle</label>
            <b-form-input v-model.trim="subtitle" placeholder="Enter subtitle"></b-form-input>
          </div>

          <div v-if="showPlacements" class="mb-3">
            <label class="control-label form-label">Placement *</label>
            <multiselect
              v-model="selectedPlacements"
              :options="placementOptions"
              :multiple="true"
              :close-on-select="false"
              placeholder="Select placements"
            ></multiselect>
          </div>

          <template v-if="isStandalone">
            <div class="mb-3">
              <label class="control-label form-label">Type</label>
              <b-form-select
                v-model="selectedType"
                :options="typeOptions"
                @change="onStandaloneTypeChange"
              ></b-form-select>
            </div>

            <div v-if="showLinkFields" class="mb-2">
              <label class="control-label form-label">URL</label>
              <b-form-input v-model.trim="url" placeholder="https://example.com"></b-form-input>
            </div>

            <div v-if="showDeeplinkFields" class="mb-2">
              <label class="control-label form-label">Deeplink</label>
              <div class="mb-3">
                <label class="control-label form-label">Screen</label>
                <select
                  v-model="deepLinkingAction"
                  class="form-control form-select"
                  @change="onChangeScreen"
                >
                  <option value="">Home screen</option>
                  <option
                    v-for="screen in deepLinkActions"
                    :key="screen.action"
                    :value="screen.action"
                  >
                    {{ screen.name }}
                  </option>
                </select>
              </div>

              <div class="mb-3" v-if="showSecondLevel">
                <label class="control-label form-label">{{ second_level_title }}</label>
                <select v-model="selectedSecondLevel" class="form-control form-select">
                  <option
                    v-for="option in secondLevelOptions"
                    :key="option.id"
                    :value="option.id"
                  >
                    {{ option.name }}
                  </option>
                </select>
              </div>
            </div>

            <div v-if="showPdfFields" class="mb-2">
              <label class="control-label form-label">Upload PDF</label>
              <input
                class="form-control"
                type="file"
                accept="application/pdf"
                @change="onPdfSelected"
              />
              <div class="text-muted mt-1" v-if="pdfFile && pdfFileName">
                Selected: {{ pdfFileName }}
              </div>
              <div class="text-muted mt-1" v-else-if="existingUrl">
                <a :href="existingUrl" target="_blank" rel="noopener noreferrer">View current PDF</a>
              </div>
            </div>
          </template>

          <template v-if="isGroup">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <label class="control-label form-label mb-0">Sub-items *</label>
              <b-button size="sm" variant="outline-primary" @click="addSubItem">
                <i class="mdi mdi-plus"></i> Add link
              </b-button>
            </div>

            <div
              v-for="(subItem, index) in subItems"
              :key="subItem.id || `new-${index}`"
              class="border rounded p-3 mb-3 bg-light"
            >
              <div class="d-flex justify-content-between align-items-center mb-2">
                <span class="fw-semibold text-muted">
                  Link {{ index + 1 }}
                  <span v-if="subItem.id" class="text-muted font-size-12">(ID: {{ subItem.id }})</span>
                </span>
                <b-button
                  v-if="subItems.length > 1"
                  size="sm"
                  variant="outline-danger"
                  @click="removeSubItem(index)"
                >
                  Remove
                </b-button>
              </div>

              <div class="mb-2">
                <label class="control-label form-label">Title *</label>
                <b-form-input v-model.trim="subItem.title" placeholder="Enter title"></b-form-input>
              </div>

              <div class="mb-2">
                <label class="control-label form-label">Subtitle</label>
                <b-form-input v-model.trim="subItem.subtitle" placeholder="Enter subtitle"></b-form-input>
              </div>

              <div class="mb-2">
                <label class="control-label form-label">Type</label>
                <b-form-select
                  v-model="subItem.type"
                  :options="subItemTypeOptions"
                  @change="onSubItemTypeChange(subItem)"
                ></b-form-select>
              </div>

              <div v-if="subItem.type === 'link'" class="mb-2">
                <label class="control-label form-label">URL *</label>
                <b-form-input v-model.trim="subItem.url" placeholder="https://example.com"></b-form-input>
              </div>

              <div v-if="subItem.type === 'deeplink'" class="mb-2">
                <label class="control-label form-label">Screen</label>
                <select
                  v-model="subItem.deepLinkingAction"
                  class="form-control form-select"
                  @change="onSubItemChangeScreen(subItem)"
                >
                  <option value="">Home screen</option>
                  <option
                    v-for="screen in deepLinkActions"
                    :key="screen.action"
                    :value="screen.action"
                  >
                    {{ screen.name }}
                  </option>
                </select>

                <div class="mt-2" v-if="subItem.showSecondLevel">
                  <label class="control-label form-label">{{ subItem.second_level_title }}</label>
                  <select v-model="subItem.selectedSecondLevel" class="form-control form-select">
                    <option
                      v-for="option in subItem.secondLevelOptions"
                      :key="option.id"
                      :value="option.id"
                    >
                      {{ option.name }}
                    </option>
                  </select>
                </div>
              </div>

              <div v-if="subItem.type === 'pdf'" class="mb-2">
                <label class="control-label form-label">Upload PDF</label>
                <input
                  class="form-control"
                  type="file"
                  accept="application/pdf"
                  @change="onSubItemPdfSelected($event, subItem)"
                />
                <div class="text-muted mt-1" v-if="subItem.pdfFile && subItem.pdfFileName">
                  Selected: {{ subItem.pdfFileName }}
                </div>
                <div class="text-muted mt-1" v-else-if="subItem.existingUrl">
                  <a :href="subItem.existingUrl" target="_blank" rel="noopener noreferrer">View current PDF</a>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </form>

    <template v-slot:modal-footer>
      <b-button variant="light" @click="closeModal">Cancel</b-button>
      <b-button
        variant="primary"
        @click="saveApplicationLink"
        :disabled="tryingToSubmit || isLoading"
      >
        <b-spinner v-show="tryingToSubmit" small></b-spinner>
        Save
      </b-button>
    </template>
  </b-modal>
</template>
